import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loader2, AlertTriangle } from "lucide-react";
import { StatusBadge } from "./StatusBadge";

const Stat = ({ label, value, testId }) => (
  <Card className="border-slate-200 p-4 shadow-sm" data-testid={testId}>
    <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
    <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{value}</div>
  </Card>
);

export function GovOverview({ meta }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/governance/overview").then((r) => setD(r.data)); }, []);
  if (!d) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-accent" />;

  return (
    <div className="space-y-6" data-testid="gov-overview">
      <Card className="border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="gov-disclaimer">
        <div className="flex gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            This centre records governance evidence for {meta.provider.product}. It does not certify compliance. All documents are drafts until
            approved by the owner after qualified UK legal review. Provider: <b>{meta.provider.legal_name}</b> — {meta.provider.role}.
          </p>
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Processing activities" value={d.activities_total} testId="stat-activities" />
        <Stat label="Subprocessors" value={d.subprocessors_total} testId="stat-subprocessors" />
        <Stat label="Documents" value={d.documents_total} testId="stat-documents" />
        <Stat label="DPIA open risks" value={d.dpia_open_risks} testId="stat-dpia-risks" />
        <Stat label="Audit entries" value={d.audit_entries} testId="stat-audit" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-200 p-5 shadow-sm">
          <h3 className="font-heading font-bold text-slate-900">Controls by status ({d.evidence_total})</h3>
          <div className="mt-3 space-y-2">
            {meta.evidence_statuses.map((s) => (
              <div key={s} className="flex items-center justify-between text-sm">
                <StatusBadge status={s} />
                <span className="font-bold text-slate-700" data-testid={`evidence-count-${s}`}>{d.evidence_by_status[s] || 0}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="border-slate-200 p-5 shadow-sm">
          <h3 className="font-heading font-bold text-slate-900">Open gaps (priority order)</h3>
          <div className="mt-3 space-y-2" data-testid="open-gaps">
            {d.open_gaps.map((g) => (
              <div key={g.id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 text-sm last:border-0">
                <div>
                  <div className="font-semibold text-slate-800">{g.requirement}</div>
                  <div className="text-xs text-slate-500">{[g.framework, g.priority].filter(Boolean).join(" · ")}</div>
                </div>
                <StatusBadge status={g.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
